<script setup>
  import { computed, ref, useId } from 'vue';
  import { useI18n } from 'vue-i18n';
  import useChartViewSettings from '../../composables/useChartViewSettings';
  import { formatPercent } from '../../utils/format_percent';

  const { t } = useI18n();
  const i18nScope = 'components.sales_dashboard';

  const props = defineProps({
    // [{ bucket: '2026-01-01', value: 12 }] -- the one-series shorthand
    points: { type: Array, default: () => [] },
    // The same measure over the run of periods just before the range, one
    // point per point of `points`, paired by position. Optional: with nothing
    // here the comparison toggle is not offered.
    previousPoints: { type: Array, default: () => [] },
    // [{ key: 'sales', label: 'Sales count', points: [...] }], for two or more
    // series. They share one value axis, so only ever pass series measured in
    // the same unit; a second scale would invent a correlation. A legend names
    // them, since colour alone cannot carry identity.
    //
    // A series that is part of another carries `shareOf`, the key of the one
    // it is a subset of. Its readouts then carry the share of that series it
    // makes up, alongside the count: a subset count on its own is hard to
    // place against the total it belongs to.
    //
    // A series may carry `previousPoints`, as the shorthand does above.
    series: { type: Array, default: () => [] },
    granularity: { type: String, default: 'month' },
    // 'count' renders whole numbers, 'currency' renders two decimals
    format: { type: String, default: 'count' },
    currency: { type: String, default: null },
    // localStorage key under which the view toggles below are remembered
    // across reloads, per browser. Without one they last for the page only.
    storageKey: { type: String, default: null }
  });

  // A viewBox keeps the drawing responsive: the SVG scales with its container
  // while the geometry below stays in fixed user units.
  const WIDTH = 720;
  const HEIGHT = 260;
  const PADDING = { top: 16, right: 16, bottom: 40, left: 56 };

  const plotWidth = WIDTH - PADDING.left - PADDING.right;
  const plotHeight = HEIGHT - PADDING.top - PADDING.bottom;

  // SVG cannot size a box to its text, and measuring via getBBox on every
  // hover is more machinery than this needs, so the width is estimated from
  // the label length. These labels are short: a date and a number.
  const TOOLTIP_LINE_HEIGHT = 16;
  const TOOLTIP_PADDING_Y = 5;
  const TOOLTIP_CHAR_WIDTH = 6.9;
  const TOOLTIP_PADDING_X = 9;
  const TOOLTIP_SWATCH = 7;
  const TOOLTIP_GAP = 10;

  const hovered = ref(null);

  // --- view toggles --------------------------------------------------------

  // `cumulative`: per period, or the running total from the first period of
  // the range. Applies to every series drawn, the previous-period ones
  // included, each accumulated over its own range so the two remain
  // comparable.
  //
  // `compared` and `hidden`: which series are drawn beside their
  // previous-period twin, and which are hidden altogether, by series key.
  // Keyed rather than indexed so a toggle survives a reload of the data,
  // whose series keep their keys. Hiding is only offered on a chart with a
  // legend: a lone series has nothing to be told apart from, and hiding it
  // would leave an empty plot.
  //
  // All three are remembered across reloads when the chart has a storage key.
  const { cumulative, compared, hidden } = useChartViewSettings(props.storageKey);

  const modeName = useId();
  const perPeriodId = useId();
  const cumulativeId = useId();

  // --- series --------------------------------------------------------------

  // One shape downstream, whether the caller passed one series or several.
  const chartSeries = computed(() => {
    if (props.series.length > 0) return props.series;
    return [{ key: 'default', label: null, points: props.points, previousPoints: props.previousPoints }];
  });

  const hasLegend = computed(() => chartSeries.value.length > 1);

  function previousPointsOf(entry) {
    // Never longer than the series itself, so a stray extra point cannot be
    // drawn past the right edge of the plot.
    return (entry.previousPoints ?? []).slice(0, entry.points.length);
  }

  function hasPrevious(entry) {
    return previousPointsOf(entry).length > 0;
  }

  const canCompare = computed(() => chartSeries.value.some(hasPrevious));

  function isCompared(entry) {
    return hasPrevious(entry) && compared[entry.key] === true;
  }

  function isShown(entry) {
    return !hasLegend.value || hidden[entry.key] !== true;
  }

  function setShown(entry, shown) {
    hidden[entry.key] = !shown;
  }

  function previousKey(key) {
    return `${key}:previous`;
  }

  function accumulate(points) {
    let total = 0;
    return points.map((point) => ({ ...point, value: (total += point.value) }));
  }

  function displayed(points) {
    return cumulative.value ? accumulate(points) : points;
  }

  // Every series the chart knows of, drawn or not: each series the caller
  // passed, in view mode, followed by its previous-period twin when it has
  // one. A twin keeps its original's colour and label, and a subset's twin is
  // a subset of its base's twin, so its shares are read against the previous
  // period rather than the current one. A series is only `drawn` while it is
  // not hidden, and its twin only once its toggle is on, but every one is
  // always here, so a share can be worked out even when the base it is read
  // against is not on screen.
  const allSeries = computed(() =>
    chartSeries.value.flatMap((entry, seriesIndex) => {
      const current = {
        key: entry.key,
        label: entry.label,
        shareOf: entry.shareOf ?? null,
        seriesIndex: seriesIndex,
        previous: false,
        drawn: isShown(entry),
        points: displayed(entry.points)
      };
      if (!hasPrevious(entry)) return [current];

      return [
        current,
        {
          key: previousKey(entry.key),
          label: entry.label,
          shareOf: entry.shareOf ? previousKey(entry.shareOf) : null,
          seriesIndex: seriesIndex,
          previous: true,
          drawn: isCompared(entry),
          points: displayed(previousPointsOf(entry))
        }
      ];
    })
  );

  const drawnSeries = computed(() => allSeries.value.filter((entry) => entry.drawn));
  const drawnCurrent = computed(() => drawnSeries.value.filter((entry) => !entry.previous));
  const drawnPrevious = computed(() => drawnSeries.value.filter((entry) => entry.previous));

  // Every series covers the same buckets -- the service fills the gaps -- so
  // the period axis is read off the longest of them.
  const axisPoints = computed(() =>
    chartSeries.value.reduce((longest, entry) => (entry.points.length > longest.length ? entry.points : longest), [])
  );

  const periodCount = computed(() => axisPoints.value.length);

  // A single line keeps its area fill. Filling under several lines stacks
  // translucent washes over one another and reads as a third colour, so the
  // lines then stand on their own. A previous-period twin counts as a line.
  const filled = computed(() => drawnSeries.value.length === 1);

  const maxValue = computed(() => {
    const values = drawnSeries.value.flatMap((entry) => entry.points.map((point) => point.value));
    const highest = Math.max(0, ...values);
    return highest === 0 ? 1 : highest;
  });

  // Round the axis top up to something a reader can divide by eye.
  const axisMax = computed(() => {
    const magnitude = 10 ** Math.floor(Math.log10(maxValue.value));
    return Math.ceil(maxValue.value / magnitude) * magnitude;
  });

  const ticks = computed(() => {
    const count = 4;
    return Array.from({ length: count + 1 }, (unused, index) => {
      const value = (axisMax.value / count) * index;
      return { value, y: yFor(value) };
    });
  });

  function xFor(index) {
    if (periodCount.value <= 1) return PADDING.left + plotWidth / 2;
    return PADDING.left + (plotWidth / (periodCount.value - 1)) * index;
  }

  function yFor(value) {
    return PADDING.top + plotHeight - (value / axisMax.value) * plotHeight;
  }

  function linePath(points) {
    return points.map((point, index) => `${index === 0 ? 'M' : 'L'}${xFor(index)},${yFor(point.value)}`).join(' ');
  }

  function areaPath(points) {
    if (points.length === 0) return '';
    const baseline = yFor(0);
    return `${linePath(points)} L${xFor(points.length - 1)},${baseline} L${xFor(0)},${baseline} Z`;
  }

  // Only ever label a subset, otherwise the axis becomes unreadable.
  const labelledIndexes = computed(() => {
    const total = periodCount.value;
    if (total === 0) return [];
    const step = Math.ceil(total / 8);
    const indexes = [];
    for (let index = 0; index < total; index += step) indexes.push(index);
    if (indexes[indexes.length - 1] === total - 1) return indexes;

    // The last period is always labelled. When the stride landed just short of
    // it, that label would be printed on top of its neighbour's, so the
    // neighbour gives way rather than the two overlapping.
    if (indexes.length > 1 && total - 1 - indexes[indexes.length - 1] < step / 2) indexes.pop();
    indexes.push(total - 1);

    return indexes;
  });

  // --- formatting ----------------------------------------------------------

  function formatValue(value) {
    if (props.format === 'currency') {
      return new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
    }
    return new Intl.NumberFormat().format(value);
  }

  function formatAxisValue(value) {
    if (value >= 1000000) return `${Math.round(value / 100000) / 10}M`;
    if (value >= 1000) return `${Math.round(value / 100) / 10}k`;
    return formatValue(value);
  }

  // Dates are rendered as UTC: the buckets are calendar dates, not instants,
  // so a local timezone would shift them a day.
  function formatBucket(bucket) {
    const date = new Date(`${bucket}T00:00:00Z`);
    const options = { timeZone: 'UTC' };

    if (props.granularity === 'year') {
      return date.toLocaleDateString(undefined, { ...options, year: 'numeric' });
    }
    if (props.granularity === 'month') {
      return date.toLocaleDateString(undefined, { ...options, year: '2-digit', month: 'short' });
    }
    return date.toLocaleDateString(undefined, { ...options, year: '2-digit', month: 'short', day: 'numeric' });
  }

  function formatAmount(value) {
    return props.currency ? `${formatValue(value)} ${props.currency.toUpperCase()}` : formatValue(value);
  }

  function valueAt(entry, index) {
    return entry.points[index]?.value ?? 0;
  }

  // The share a subset series makes up of the series it is part of, for the
  // period at `index`. Null when the series is not a subset, or when the one
  // it belongs to is zero for that period: no share is a truthful reading of
  // nothing out of nothing, and it keeps the division off zero.
  function shareAt(entry, index) {
    if (!entry.shareOf) return null;

    const base = allSeries.value.find((candidate) => candidate.key === entry.shareOf);
    if (!base) return null;

    const total = valueAt(base, index);
    if (!total) return null;

    return valueAt(entry, index) / total;
  }

  // What one series reads as for one period, shared by the tooltip and the
  // keyboard readout so the two can never say different things.
  function seriesValueAt(entry, index) {
    const amount = formatAmount(valueAt(entry, index));
    const share = shareAt(entry, index);

    return share === null ? amount : `${amount} (${formatPercent(share)})`;
  }

  function namedValueAt(entry, index) {
    const amount = seriesValueAt(entry, index);
    return entry.label ? `${entry.label}: ${amount}` : amount;
  }

  // The bucket the previous-period twins hold at `index`. All twins share the
  // same buckets, so the first drawn one speaks for them all.
  function previousBucketAt(index) {
    return drawnPrevious.value[0]?.points[index]?.bucket;
  }

  function previousPeriodHeading(index) {
    return `${t(`${i18nScope}.previous_period`)} (${formatBucket(previousBucketAt(index))})`;
  }

  // What a hover, a keyboard focus and the tooltip all read out. With several
  // series each is named, so the readout never depends on matching colours.
  // The previous period, when drawn, follows under its own date.
  function periodLabel(index) {
    const period = formatBucket(axisPoints.value[index].bucket);
    if (drawnSeries.value.length === 0) return period;

    const values = drawnCurrent.value.map((entry) => namedValueAt(entry, index));
    const label = values.length === 0 ? period : `${period}: ${values.join(', ')}`;
    if (drawnPrevious.value.length === 0) return label;

    const previousValues = drawnPrevious.value.map((entry) => namedValueAt(entry, index));
    return `${label}. ${previousPeriodHeading(index)}: ${previousValues.join(', ')}`;
  }

  const tooltip = computed(() => {
    if (hovered.value === null || drawnSeries.value.length === 0) return null;
    const point = axisPoints.value[hovered.value];
    if (!point) return null;
    const index = hovered.value;

    // One line per series, headed by the period, rather than one long line:
    // two series on a single line push the box past the plot edges. The
    // previous period gets its own heading, since its values sit under
    // another date than the one hovered.
    const multiline = hasLegend.value || drawnPrevious.value.length > 0;
    const lines = multiline
      ? [{ key: 'period', text: formatBucket(point.bucket), series: null }]
          .concat(drawnCurrent.value.map((entry) => ({ key: entry.key, text: namedValueAt(entry, index), series: entry })))
          .concat(
            drawnPrevious.value.length > 0
              ? [{ key: 'previous-period', text: previousPeriodHeading(index), series: null }]
              : []
          )
          .concat(drawnPrevious.value.map((entry) => ({ key: entry.key, text: namedValueAt(entry, index), series: entry })))
      : [{ key: 'value', text: periodLabel(index), series: null }];

    const swatchRoom = multiline ? TOOLTIP_SWATCH + 5 : 0;
    const width = Math.max(...lines.map((line) => line.text.length)) * TOOLTIP_CHAR_WIDTH + TOOLTIP_PADDING_X * 2 + swatchRoom;
    const height = lines.length * TOOLTIP_LINE_HEIGHT + TOOLTIP_PADDING_Y * 2;
    const anchor = xFor(index);
    const markY = Math.min(...drawnSeries.value.map((entry) => yFor(valueAt(entry, index))));

    // Sit above the highest mark, but flip below it when there is no room, and
    // stay within the drawing so the box never clips at an edge.
    const flipped = markY - height - TOOLTIP_GAP < 0;
    const x = clamp(anchor - width / 2, 2, WIDTH - width - 2);

    return {
      lines: lines.map((line, lineIndex) => ({
        ...line,
        // Centred in its own line box, so the swatch and the text line up.
        y: TOOLTIP_PADDING_Y + lineIndex * TOOLTIP_LINE_HEIGHT + TOOLTIP_LINE_HEIGHT / 2
      })),
      width: width,
      height: height,
      textX: x + TOOLTIP_PADDING_X + swatchRoom,
      swatchX: x + TOOLTIP_PADDING_X,
      x: x,
      y: flipped ? markY + TOOLTIP_GAP : markY - height - TOOLTIP_GAP,
      anchorX: anchor
    };
  });

  function clamp(value, lowest, highest) {
    return Math.min(Math.max(value, lowest), Math.max(lowest, highest));
  }

  function compareLabel(entry) {
    if (!hasLegend.value) return t(`${i18nScope}.compare_previous_period`);
    return t(`${i18nScope}.compare_series_previous_period`, { series: entry.label });
  }
</script>

<template>
  <div class="time-series-chart">
    <div v-if="periodCount > 0" class="chart-controls">
      <!-- Per period or running total. A radio group rather than a switch:
           both states are named, so neither has to be inferred from the other. -->
      <div class="btn-group btn-group-sm" role="group" :aria-label="t(`${i18nScope}.value_mode`)">
        <input :id="perPeriodId" v-model="cumulative" type="radio" class="btn-check" :name="modeName" :value="false" />
        <label class="btn btn-outline-secondary" :for="perPeriodId">{{ t(`${i18nScope}.per_period`) }}</label>
        <input :id="cumulativeId" v-model="cumulative" type="radio" class="btn-check" :name="modeName" :value="true" />
        <label class="btn btn-outline-secondary" :for="cumulativeId">{{ t(`${i18nScope}.cumulative`) }}</label>
      </div>

      <!-- Identity carried by a named key, never by colour alone. With several
           series each entry is a toggle that hides or shows its line, and each
           also offers its previous-period twin, drawn in the same colour but
           dashed, which is what the second swatch shows. -->
      <ul v-if="hasLegend || canCompare" class="chart-legend list-unstyled">
        <li v-for="(entry, seriesIndex) in chartSeries" :key="entry.key" :class="`series-${seriesIndex}`">
          <label v-if="hasLegend" class="legend-toggle form-check form-check-inline">
            <input class="form-check-input" type="checkbox" :checked="isShown(entry)" @change="setShown(entry, $event.target.checked)" />
            <span class="legend-swatch" aria-hidden="true"></span>
            <span class="form-check-label">{{ entry.label }}</span>
          </label>
          <label v-if="hasPrevious(entry)" class="legend-toggle form-check form-check-inline">
            <input v-model="compared[entry.key]" class="form-check-input" type="checkbox" :aria-label="compareLabel(entry)" />
            <span class="legend-swatch legend-swatch-previous" aria-hidden="true"></span>
            <span class="form-check-label">
              {{ hasLegend ? t(`${i18nScope}.previous_period`) : t(`${i18nScope}.compare_previous_period`) }}
            </span>
          </label>
        </li>
      </ul>
    </div>

    <svg
      v-if="periodCount > 0"
      :viewBox="`0 0 ${WIDTH} ${HEIGHT}`"
      class="chart-svg"
      preserveAspectRatio="xMidYMid meet"
      role="presentation"
      focusable="false"
    >
      <!-- Horizontal gridlines and value axis -->
      <g class="gridlines">
        <line
          v-for="tick in ticks"
          :key="`grid-${tick.value}`"
          :x1="PADDING.left"
          :x2="WIDTH - PADDING.right"
          :y1="tick.y"
          :y2="tick.y"
        />
      </g>
      <g class="axis-labels">
        <text
          v-for="tick in ticks"
          :key="`tick-${tick.value}`"
          :x="PADDING.left - 8"
          :y="tick.y + 4"
          text-anchor="end"
        >{{ formatAxisValue(tick.value) }}</text>
      </g>

      <!-- Series. A previous-period twin shares its original's colour and is
           told apart by its dashed line and hollow dots. -->
      <g
        v-for="entry in drawnSeries"
        :key="`series-${entry.key}`"
        :class="[`series-${entry.seriesIndex}`, { 'is-previous': entry.previous }]"
      >
        <path v-if="filled" class="area" :d="areaPath(entry.points)" />
        <path class="line" :d="linePath(entry.points)" />
        <circle
          v-for="(point, index) in entry.points"
          :key="`dot-${point.bucket}`"
          class="dot"
          :class="{ 'is-hovered': hovered === index }"
          :cx="xFor(index)"
          :cy="yFor(point.value)"
          :r="hovered === index ? 5 : 3"
        />
      </g>

      <!-- Period axis -->
      <g class="axis-labels">
        <text
          v-for="index in labelledIndexes"
          :key="`label-${index}`"
          :x="xFor(index)"
          :y="HEIGHT - PADDING.bottom + 20"
          text-anchor="middle"
        >{{ formatBucket(axisPoints[index].bucket) }}</text>
      </g>

      <!-- Hit areas sit above the marks so a hover anywhere in a period's
           column registers, whichever series it lands on. One focusable target
           per period gives keyboard users the same readout as a mouse hover. -->
      <rect
        v-for="(point, index) in axisPoints"
        :key="`hit-${point.bucket}`"
        class="hit-area"
        tabindex="0"
        role="img"
        :aria-label="periodLabel(index)"
        :x="PADDING.left + (plotWidth / periodCount) * index"
        :y="PADDING.top"
        :width="plotWidth / periodCount"
        :height="plotHeight"
        @mouseenter="hovered = index"
        @mouseleave="hovered = null"
        @focus="hovered = index"
        @blur="hovered = null"
      />

      <!-- Tooltip. Deliberately not called "tooltip": Bootstrap ships a global
           .tooltip rule with opacity 0, which would render this invisible. -->
      <g v-if="tooltip" class="chart-tooltip" aria-hidden="true">
        <line
          class="chart-tooltip-rule"
          :x1="tooltip.anchorX"
          :x2="tooltip.anchorX"
          :y1="PADDING.top"
          :y2="HEIGHT - PADDING.bottom"
        />
        <rect
          class="chart-tooltip-box"
          :x="tooltip.x"
          :y="tooltip.y"
          :width="tooltip.width"
          :height="tooltip.height"
          rx="4"
        />
        <template v-for="line in tooltip.lines" :key="`tooltip-${line.key}`">
          <rect
            v-if="line.series"
            class="chart-tooltip-swatch"
            :class="[`series-${line.series.seriesIndex}`, { 'is-previous': line.series.previous }]"
            :x="tooltip.swatchX"
            :y="tooltip.y + line.y - TOOLTIP_SWATCH / 2"
            :width="TOOLTIP_SWATCH"
            :height="TOOLTIP_SWATCH"
            rx="1.5"
          />
          <text
            class="chart-tooltip-text"
            :class="{ 'is-heading': !line.series }"
            :x="tooltip.textX"
            :y="tooltip.y + line.y"
            dominant-baseline="central"
          >{{ line.text }}</text>
        </template>
      </g>
    </svg>
  </div>
</template>

<style scoped>
  .time-series-chart {
    width: 100%;

    /* Validated against the card surface for contrast and for colour-vision
       deficiency separation: blue to orange to purple stays legible for
       protanopia, deuteranopia and tritanopia alike, pair by pair. The next
       three -- teal, magenta, olive -- keep the contrast and stay apart from
       the first three by lightness as well as hue, since six hues cannot all
       be told apart under every deficiency; a series is always named in the
       legend and the readouts, never identified by its colour alone. */
    --series-0-color: #0d6efd;
    --series-0-strong: #0a58ca;
    --series-0-area: rgba(13, 110, 253, 0.12);
    --series-1-color: #e8590c;
    --series-1-strong: #ca6510;
    --series-1-area: rgba(232, 89, 12, 0.12);
    --series-2-color: #59359a;
    --series-2-strong: #472b7b;
    --series-2-area: rgba(89, 53, 154, 0.12);
    --series-3-color: #0f766e;
    --series-3-strong: #0b5e57;
    --series-3-area: rgba(15, 118, 110, 0.12);
    --series-4-color: #b02a72;
    --series-4-strong: #8e215c;
    --series-4-area: rgba(176, 42, 114, 0.12);
    --series-5-color: #8a6d00;
    --series-5-strong: #6b5500;
    --series-5-area: rgba(138, 109, 0, 0.12);
  }

  .chart-svg {
    width: 100%;
    height: auto;
    overflow: visible;
  }

  .chart-controls {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.5rem 1rem;
    margin-bottom: 0.5rem;
  }

  .chart-legend {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem 1.25rem;
    justify-content: flex-end;
    margin: 0 0 0 auto;
    font-size: 0.8125rem;
    color: #6c757d;
  }

  .chart-legend li {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.25rem 0.5rem;
  }

  .legend-swatch {
    display: inline-block;
    width: 0.625rem;
    height: 0.625rem;
    margin-right: 0.375rem;
    border-radius: 2px;
    vertical-align: baseline;
  }

  .legend-toggle {
    margin: 0;
    padding-left: 1.5em;
    font-size: inherit;
  }

  .legend-toggle .form-check-input {
    margin-top: 0.15em;
  }

  .legend-toggle .form-check-label {
    cursor: pointer;
  }

  .gridlines line {
    stroke: #dee2e6;
    stroke-width: 1;
  }

  .axis-labels text {
    fill: #6c757d;
    font-size: 11px;
  }

  .series-0 {
    --series-color: var(--series-0-color);
    --series-strong: var(--series-0-strong);
    --series-area: var(--series-0-area);
  }

  .series-1 {
    --series-color: var(--series-1-color);
    --series-strong: var(--series-1-strong);
    --series-area: var(--series-1-area);
  }

  .series-2 {
    --series-color: var(--series-2-color);
    --series-strong: var(--series-2-strong);
    --series-area: var(--series-2-area);
  }

  .series-3 {
    --series-color: var(--series-3-color);
    --series-strong: var(--series-3-strong);
    --series-area: var(--series-3-area);
  }

  .series-4 {
    --series-color: var(--series-4-color);
    --series-strong: var(--series-4-strong);
    --series-area: var(--series-4-area);
  }

  .series-5 {
    --series-color: var(--series-5-color);
    --series-strong: var(--series-5-strong);
    --series-area: var(--series-5-area);
  }

  .legend-swatch,
  .chart-tooltip-swatch {
    background: var(--series-color);
    fill: var(--series-color);
  }

  /* The twin's swatch: same colour, but an outline, as its line is dashed.
     After the filled rule above, which it overrides at equal specificity. */
  .legend-swatch.legend-swatch-previous {
    background: transparent;
    border: 2px dashed var(--series-color);
    box-sizing: border-box;
  }

  .chart-tooltip-swatch.is-previous {
    fill: none;
    stroke: var(--series-color);
    stroke-width: 1.5;
  }

  .area {
    fill: var(--series-area);
  }

  .line {
    fill: none;
    stroke: var(--series-color);
    stroke-width: 2;
    stroke-linejoin: round;
    stroke-linecap: round;
  }

  .is-previous .line {
    stroke-dasharray: 6 4;
  }

  .dot {
    fill: var(--series-color);
  }

  .dot.is-hovered {
    fill: var(--series-strong);
  }

  .is-previous .dot {
    fill: #fff;
    stroke: var(--series-color);
    stroke-width: 1.5;
  }

  .is-previous .dot.is-hovered {
    stroke: var(--series-strong);
  }

  .chart-tooltip {
    pointer-events: none;
  }

  .chart-tooltip-rule {
    stroke: #adb5bd;
    stroke-width: 1;
    stroke-dasharray: 3 3;
  }

  .chart-tooltip-box {
    fill: #212529;
    fill-opacity: 0.92;
  }

  .chart-tooltip-text {
    fill: #fff;
    font-size: 12px;
    font-weight: 600;
  }

  .chart-tooltip-text.is-heading {
    fill: #dee2e6;
    font-weight: 400;
  }

  .hit-area {
    fill: transparent;
  }

  .hit-area:focus-visible {
    fill: rgba(13, 110, 253, 0.08);
    outline: 2px solid #0d6efd;
    outline-offset: -2px;
  }

  /* Respect a reader's reduced-motion preference: nothing here animates, but
     the hover growth on dots is a transition worth suppressing. */
  @media (prefers-reduced-motion: no-preference) {
    .dot {
      transition: r 0.12s ease;
    }
  }
</style>
